// frontend/src/pages/Result.jsx
// ✅ UPDATED: Added in-dev badge to style dropdown in EditModal
import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { ACTIVE_STYLES } from "../shared/styles.js";
import { getIdentityHeaders, refreshTokenIfNeeded } from '../lib/identityGoogle';
import { trackStyleView } from "../utils/trackStyleView.js";
import { 
  API_BASE, 
  fromNow,
  toast,
  getImageUrl 
} from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

/* --------------------------- helpers / normalize -------------------------- */
function normKey(s) {
  return String(s?.key ?? s?.slug ?? "").trim().toLowerCase();
}

// ✅ UPDATED: Include status field
const STYLES = ACTIVE_STYLES.map((s) => ({
  key: normKey(s),
  name: s?.name || "",
  status: s?.status || "ready",
}));

/* -------------------------------- page --------------------------------- */
export default function Result() {
  const { jobId } = useParams();
  const nav = useNavigate();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");

  // inline editor state
  const [showEditor, setShowEditor] = React.useState(false);
  const [editPrompt, setEditPrompt] = React.useState("");
  const [editStyle, setEditStyle] = React.useState("");
  const [editAspect, setEditAspect] = React.useState("1:1");
  const [submitting, setSubmitting] = React.useState(false);

  // --- Animate (Veo) state ---
  const [pricing, setPricing] = React.useState(null);            // { credits, durationSeconds, available }
  const [credits, setCredits] = React.useState(null);
  const [motionPrompt, setMotionPrompt] = React.useState("");
  const [animState, setAnimState] = React.useState("idle");      // idle | processing | complete | failed
  const [videoUrl, setVideoUrl] = React.useState(null);
  const [animError, setAnimError] = React.useState("");
  const pollRef = React.useRef(null);

  // Reset animate state whenever the result changes (proven lesson: stale state
  // leaks across navigations otherwise).
  React.useEffect(() => {
    setMotionPrompt("");
    setAnimState("idle");
    setVideoUrl(null);
    setAnimError("");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [jobId]);

  // Clear any active poll on unmount.
  React.useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Fetch credits (same source + sync as Create).
  const refreshCredits = React.useCallback(async () => {
    try {
      const res = await fetch(api("/api/credits"), {
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      const j = await res.json();
      if (typeof j.credits === "number") setCredits(j.credits);
    } catch {}
  }, []);

  // Load animation pricing + credits on mount.
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(api("/api/animate/pricing"), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        if (res.ok) setPricing(await res.json());
      } catch {}
      await refreshCredits();
    })();
  }, [refreshCredits]);

  // If this generation already has a video, surface it immediately.
  React.useEffect(() => {
    if (!data) return;
    const existing = data.videoUrl || data.videoGcsObject;
    if (existing) {
      setAnimState("complete");
      setVideoUrl(api(`/api/animate/video/${data.videoJobId || jobId}`));
    }
  }, [data, jobId]);

  const pollAnimation = React.useCallback((animJobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const startedAt = Date.now();
    const MAX_MS = 6 * 60 * 1000; // ~6 minutes
    pollRef.current = setInterval(async () => {
      try {
        if (Date.now() - startedAt > MAX_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimState("failed");
          setAnimError("Animation timed out. Your credits were refunded.");
          await refreshCredits();
          return;
        }
        const res = await fetch(api(`/api/animate/status/${animJobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await res.json();
        if (j.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVideoUrl(api(`/api/animate/video/${animJobId}`));
          setAnimState("complete");
        } else if (j.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimState("failed");
          setAnimError(j.error || "Animation failed. Your credits were refunded.");
          await refreshCredits();
        }
      } catch {
        // transient; keep polling
      }
    }, 10000);
  }, [refreshCredits]);

  const handleAnimate = React.useCallback(async () => {
    if (!data?.id) return;
    setAnimError("");

    // Guard token expiry first, same as Create's generate.
    const tokenValid = await refreshTokenIfNeeded();
    if (!tokenValid) {
      toast("Your session expired. Please sign in again.");
      return;
    }

    setAnimState("processing");
    try {
      const res = await fetch(api("/api/animate"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        body: JSON.stringify({ generationId: data.id, prompt: motionPrompt.trim() || undefined }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || "Failed to start animation");

      // Credits were deducted — sync across pages.
      if (typeof j.creditsRemaining === "number") setCredits(j.creditsRemaining);
      else await refreshCredits();
      try {
        localStorage.setItem("ab:credits:ping", String(Date.now()));
        window.dispatchEvent(new Event("ab:credits"));
      } catch {}

      pollAnimation(j.jobId);
    } catch (e) {
      setAnimState("failed");
      setAnimError(e.message || "Failed to start animation");
      await refreshCredits();
    }
  }, [data, motionPrompt, pollAnimation, refreshCredits]);

  // Right after the existing useEffect that loads data, add this:

// ✅ Track style view when result loads
React.useEffect(() => {
  if (data && data.style) {
    const styleSlug = String(data.style || "").toLowerCase().trim();
    if (styleSlug && styleSlug !== "default") {
      trackStyleView(styleSlug, "direct");
    }
  }
}, [data]); // Track when data changes

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(api(`/api/generations/${jobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j?.error || res.statusText);
        if (alive) {
          setData(j);
          // prime editor with current values
          setEditPrompt(j?.prompt || "");
          setEditStyle(j?.style || "");
          setEditAspect(j?.aspectRatio || "1:1");
        }
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load result");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId]);

  // ✅ ALWAYS use download endpoint for consistent streaming
// ✅ FALLBACK CHAIN (resilience against backend errors)
const imageUrl = data ? getImageUrl(data) : null;

  // Force a real file download via the backend
  const onDownload = React.useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await fetch(api(`/api/generations/${jobId}/download`), {
        method: "GET",
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `artibytes-${jobId}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Downloaded!");
    } catch (err) {
      toast(err.message || "Download failed");
    }
  }, [jobId]);

  // Keyboard shortcut
  React.useEffect(() => {
    function onKey(e) {
      if (e.target && /input|textarea/i.test(e.target.tagName)) return;
      if (e.key.toLowerCase() === "d") onDownload();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDownload]);

  function openEditor() {
    if (!data) return;
    setShowEditor(true);
  }
  
  function closeEditor() {
    setShowEditor(false);
  }

  async function applyEditsAndRegenerate() {
    if (!editPrompt.trim()) {
      toast("Please enter a prompt");
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(api("/api/generations"), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({
          prompt: editPrompt.trim(),
          style: editStyle || undefined,
          aspectRatio: editAspect || "1:1",
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || res.statusText);
      toast("Generating...");
      // navigate to the new result page
      nav(`/result/${j.id}`);
      setShowEditor(false);
    } catch (e) {
      toast(e.message || "Failed to regenerate");
    } finally {
      setSubmitting(false);
    }
  }

  const styleId = React.useMemo(() => {
    const s = String(data?.style || "").toLowerCase().trim();
    if (!s || s === "default") return null;
    return s;
  }, [data?.style]);

  return (
    <Screen>
      <header style={{ marginBottom: 8 }}>
        <h1 className="h1" style={{ marginBottom: 6 }}>Result</h1>
        {data?.prompt && (
          <p style={{ margin: 0, color: "var(--muted)" }}>
            <span style={{ color: "var(--text)" }}><b>Prompt:</b></span>{" "}
            <em>{data.prompt}</em>
          </p>
        )}
      </header>

      {/* Sticky action bar */}
      <div
        style={{
          position: "sticky",
          top: 68,
          zIndex: 5,
          background:
            "linear-gradient(180deg, rgba(255,255,255,.95), rgba(255,255,255,.75))",
          backdropFilter: "saturate(1.2) blur(4px)",
          borderRadius: 14,
          padding: "10px 12px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          border: "1px solid var(--hairline)",
          marginBottom: 14,
        }}
      >
        <button className="btn btn-primary" onClick={onDownload} disabled={!imageUrl}>
          Download
        </button>
        <button className="btn" onClick={openEditor} disabled={!data}>
          Edit prompt & style
        </button>
        <button className="btn" onClick={() => nav("/gallery")}>
          View in gallery
        </button>

        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)" }}>
          Shortcut: <kbd>D</kbd> download
        </div>
      </div>

      {/* Meta + image */}
      {loading && <Skeleton />}
      {!loading && err && <ErrorState message={err} />}
      {!loading && !err && data && (
        <div style={{ display: "grid", gap: 16 }}>
          <Meta data={data} />
          <ImageCard src={imageUrl} alt={data.prompt} />

          {/* Animate (Veo) */}
          {pricing?.available !== false && (
            <div
              style={{
                border: "1px solid var(--hairline)",
                borderRadius: 14,
                padding: 16,
                display: "grid",
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 600 }}>Animate this image</div>

              {animState === "complete" && videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  style={{ width: "100%", borderRadius: 12, background: "#000" }}
                />
              ) : (
                <>
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Optional: describe the motion (e.g. gentle wind, drifting clouds, subtle camera push)…"
                    value={motionPrompt}
                    onChange={(e) => setMotionPrompt(e.target.value)}
                    disabled={animState === "processing"}
                    style={{ resize: "vertical" }}
                  />

                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleAnimate}
                      disabled={
                        animState === "processing" ||
                        !imageUrl ||
                        (pricing && typeof credits === "number" && credits < pricing.credits)
                      }
                    >
                      {animState === "processing"
                        ? "Animating…"
                        : `Animate${pricing ? ` (${pricing.credits} credits)` : ""}`}
                    </button>

                    <div className="hint">
                      {pricing ? `${pricing.credits} credits per animation` : "Animation"}
                      {pricing?.durationSeconds ? ` • ~${pricing.durationSeconds}s` : ""}
                      {typeof credits === "number" ? (
                        <span style={{ marginLeft: 8, color: "#6B7280" }}>
                          • Available: {credits}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {animState === "processing" && (
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>
                      Generating your animation — this can take a minute or two. You can browse the app, come back in a minute or 2.
                    </div>
                  )}

                  {animState === "failed" && animError && (
                    <div className="error">{animError}</div>
                  )}

                  {pricing && typeof credits === "number" && credits < pricing.credits && (
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>
                      You need {pricing.credits} credits to animate.{" "}
                      <Link to="/profile">Top up</Link>.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Style Feedback Section - only show if style is used */}
          {styleId && (
            <StyleFeedback 
              styleId={styleId}
              generationId={data.id}
            />
          )}

          {/* Bottom actions */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/home" className="btn">
              Home
            </Link>
            <Link to="/profile" className="btn btn-primary">
              Go to Profile
            </Link>
          </div>
        </div>
      )}

      {/* Inline Modal for editing */}
      {showEditor && (
        <EditModal
          prompt={editPrompt}
          styleId={editStyle}
          aspect={editAspect}
          onPromptChange={setEditPrompt}
          onStyleChange={setEditStyle}
          onAspectChange={setEditAspect}
          onClose={closeEditor}
          onApply={applyEditsAndRegenerate}
          busy={submitting}
        />
      )}
    </Screen>
  );
}

/* ------------------------------- Pieces ---------------------------------- */

function Meta({ data }) {
  return (
    <div
      style={{
        border: "1px solid var(--hairline)",
        borderRadius: 16,
        padding: 12,
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
        background:
          "linear-gradient(180deg, rgba(250,250,255,.6), rgba(245,245,255,.35))",
      }}
    >
      <Chip label={`Style: ${data?.style ?? "default"}`} />
      <Chip label={`Aspect: ${data?.aspectRatio ?? "1:1"}`} />
      <Chip label={fromNow(data?.createdAt)} />
    </div>
  );
}

function Chip({ label }) {
  return (
    <span
      style={{
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid var(--hairline)",
        background: "white",
      }}
    >
      {label}
    </span>
  );
}

function ImageCard({ src, alt }) {
  return (
    <div
      style={{
        borderRadius: 24,
        overflow: "hidden",
        border: "1px solid var(--hairline)",
        boxShadow: "0 10px 25px rgba(0,0,0,.06), 0 2px 8px rgba(0,0,0,.04)",
        background:
          "radial-gradient(1200px 400px at 50% -10%, rgba(128,78,255,.11), transparent 60%)",
        minHeight: 400,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt || "Result image"}
          style={{ width: "100%", display: "block", background: "#fafafa" }}
          loading="eager"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          No image URL available.
        </div>
      )}
    </div>
  );
}

/* ----------------------- Style Feedback Component ----------------------- */
function StyleFeedback({ styleId, generationId }) {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [hasLiked, setHasLiked] = React.useState(false);
  const [liking, setLiking] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);

  // Fetch style stats + user's like status
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        
        // ✅ MATCH OTHER ENDPOINTS - use fetch + getIdentityHeaders
        const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/stats`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        
        const statsData = await res.json();
        if (!res.ok) throw new Error(statsData?.error || "Failed to fetch stats");
        
        if (alive) {
          setStats(statsData);
          setHasLiked(statsData.userHasLiked || false);
        }
      } catch (e) {
        console.warn("Failed to fetch style stats:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    
    return () => { alive = false; };
  }, [styleId]);

  const handleLike = React.useCallback(async () => {
    if (hasLiked || liking) return;
    
    try {
      setLiking(true);
      
      // ✅ MATCH OTHER ENDPOINTS - use fetch + getIdentityHeaders
      const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/like`), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({ generationId }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to like style");
      
      // Update local state
      setHasLiked(true);
      setStats(prev => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
        userHasLiked: true,
      }));
      
      toast("Thanks for your feedback! ❤️");
    } catch (e) {
      toast(e.message || "Failed to like style");
    } finally {
      setLiking(false);
    }
  }, [styleId, generationId, hasLiked, liking]);

  const handleReport = React.useCallback(async () => {
    console.log('[DEBUG] handleReport called');
    console.log('[DEBUG] styleId:', styleId);
    console.log('[DEBUG] generationId:', generationId);
    
    const reason = window.prompt(
      "What issue did you encounter with this style?\n\n" +
      "Examples: Poor quality, doesn't match description, inappropriate content"
    );
    
    console.log('[DEBUG] reason:', reason);
    
    if (!reason || !reason.trim()) {
      console.log('[DEBUG] No reason provided, returning');
      return;
    }
    
    try {
      setReporting(true);
      console.log('[DEBUG] About to fetch...');
      console.log('[DEBUG] URL:', api(`/api/feedback/styles/${encodeURIComponent(styleId)}/report`));
      console.log('[DEBUG] Headers:', { 
        "Content-Type": "application/json",
        ...getIdentityHeaders() 
      });
      
      // ✅ MATCH OTHER ENDPOINTS - use fetch + getIdentityHeaders
      const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/report`), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({ 
          generationId,
          reason: reason.trim(),
        }),
      });
      
      console.log('[DEBUG] Fetch completed, status:', res.status);
      
      const data = await res.json();
      console.log('[DEBUG] Response data:', data);
      
      if (!res.ok) throw new Error(data?.error || "Failed to report issue");
      
      toast("Thank you for reporting. We'll review this.");
    } catch (e) {
      console.error('[DEBUG] Error:', e);
      toast(e.message || "Failed to report issue");
    } finally {
      setReporting(false);
    }
  }, [styleId, generationId]);

  if (loading) {
    return (
      <div className="style-feedback">
        <div className="sf-skeleton" />
      </div>
    );
  }

  const reputation = stats?.reputation;
  const likes = stats?.likes || 0;
  const usageCount = stats?.usageCount || 0;

  return (
    <>
      <StyleFeedbackTag />
      <div className="style-feedback">
        <div className="sf-header">
          <div className="sf-title">Style Feedback</div>
          <div className="sf-style-name">{styleId}</div>
        </div>

        <div className="sf-message">
          By using this style you are supporting an <strong>independent artist</strong>
        </div>

        <div className="sf-stats-row">
          <div className="sf-stat">
            <div className="sf-stat-value">{usageCount.toLocaleString()}</div>
            <div className="sf-stat-label">Uses</div>
          </div>
          
          <div className="sf-stat">
            <div className="sf-stat-value">{likes.toLocaleString()}</div>
            <div className="sf-stat-label">Likes</div>
          </div>

          {reputation && (
            <div className="sf-reputation">
              <ReputationBadge reputation={reputation} />
            </div>
          )}
        </div>

        <div className="sf-actions">
          <button 
            className={`sf-btn sf-btn-like ${hasLiked ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={hasLiked || liking}
          >
            {hasLiked ? '❤️ Liked' : '🤍 Love this style'}
          </button>

          <button 
            className="sf-btn-report"
            onClick={handleReport}
            disabled={reporting}
          >
            ⚠️ Report issue
          </button>
        </div>
      </div>
    </>
  );
}

/* Reputation badge component */
function ReputationBadge({ reputation }) {
  if (!reputation || typeof reputation.score !== 'number') return null;

  const { score, level } = reputation;
  
  const levelConfig = {
    emerging: { label: "Emerging", color: "#10B981", icon: "🌱" },
    established: { label: "Established", color: "#3B82F6", icon: "⭐" },
    verified: { label: "Verified", color: "#8B5CF6", icon: "✓" },
  };

  const config = levelConfig[level] || levelConfig.emerging;

  return (
    <div 
      className="reputation-badge"
      style={{ borderColor: config.color }}
      title={`Reputation: ${score}/100`}
    >
      <span className="reputation-icon">{config.icon}</span>
      <span className="reputation-label" style={{ color: config.color }}>
        {config.label}
      </span>
    </div>
  );
}

function StyleFeedbackTag() {
  return (
    <style>{`
.style-feedback {
  background: linear-gradient(135deg, #F3F0FF 0%, #FAF5FF 100%);
  border: 1px solid #E9D5FF;
  border-radius: 16px;
  padding: 16px;
  display: grid;
  gap: 12px;
}

.sf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sf-title {
  font-size: 14px;
  font-weight: 800;
  color: #1F2937;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sf-style-name {
  background: #6D5EF9;
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.sf-message {
  color: #1F2937;
  font-size: 14px;
  line-height: 1.5;
}

.sf-message strong {
  color: #6D5EF9;
  font-weight: 800;
}

.sf-stats-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 0;
  border-top: 1px solid #E9D5FF;
  border-bottom: 1px solid #E9D5FF;
}

.sf-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sf-stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #6D5EF9;
  line-height: 1;
}

.sf-stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sf-reputation {
  margin-left: auto;
}

.reputation-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #FFFFFF;
  border: 1.5px solid;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: help;
}

.reputation-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.reputation-icon {
  font-size: 12px;
  line-height: 1;
}

.reputation-label {
  line-height: 1;
}

.sf-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.sf-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 2px solid #6D5EF9;
  background: #fff;
  color: #6D5EF9;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sf-btn:hover:not(:disabled) {
  background: #6D5EF9;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.3);
}

.sf-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sf-btn.liked {
  background: #6D5EF9;
  color: #fff;
  border-color: #6D5EF9;
}

.sf-btn-report {
  height: 40px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: #6B7280;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.sf-btn-report:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  color: #DC2626;
}

.sf-btn-report:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sf-skeleton {
  height: 180px;
  background: linear-gradient(90deg, #f5f5f7, #ececf2, #f5f5f7);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 16px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
    `}</style>
  );
}

function Skeleton() {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div
        style={{
          height: 20,
          width: 240,
          background: "var(--skeleton)",
          borderRadius: 6,
        }}
      />
      <div
        style={{
          height: 420,
          background: "var(--skeleton)",
          borderRadius: 22,
        }}
      />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      style={{
        border: "1px solid #f2c2c2",
        background: "#fff7f7",
        color: "#8a1f1f",
        padding: 16,
        borderRadius: 12,
      }}
    >
      {message || "Something went wrong."}
    </div>
  );
}

/* ------------------------------ Edit Modal ------------------------------- */

function EditModal({
  prompt,
  styleId,
  aspect,
  onPromptChange,
  onStyleChange,
  onAspectChange,
  onClose,
  onApply,
  busy,
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,10,20,.35)",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(820px, 96vw)",
          background: "white",
          borderRadius: 16,
          border: "1px solid var(--hairline)",
          boxShadow: "0 20px 55px rgba(0,0,0,.18)",
          padding: 16,
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 className="h3" style={{ margin: 0 }}>
            Edit prompt & style
          </h3>
          <button className="btn btn-xs" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Prompt</span>
          <textarea
            rows={4}
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid var(--hairline)",
              padding: 10,
              resize: "vertical",
            }}
            placeholder="Describe your image…"
          />
        </label>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 6, flex: "1 1 220px" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Style</span>
            <select
              value={styleId}
              onChange={(e) => onStyleChange(e.target.value)}
              style={{
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                padding: "8px 10px",
                background: "white",
              }}
            >
              <option value="">None (Default)</option>
              {/* ✅ UPDATED: Add in-dev emoji indicator */}
              {STYLES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name} {s.status === "in-dev" ? "🚧" : ""}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: 6, flex: "0 0 160px" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Aspect ratio</span>
            <select
              value={aspect}
              onChange={(e) => onAspectChange(e.target.value)}
              style={{
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                padding: "8px 10px",
                background: "white",
              }}
            >
              <option value="1:1">1:1</option>
              <option value="16:9">16:9</option>
              <option value="9:16">9:16</option>
              <option value="4:3">4:3</option>
              <option value="3:4">3:4</option>
            </select>
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--muted)", marginRight: "auto" }}>
            Regenerating will spend 1 credit.
          </span>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onApply}
            disabled={busy}
            style={{ minWidth: 160 }}
          >
            {busy ? "Generating…" : "Apply & Re-generate"}
          </button>
        </div>
      </div>
    </div>
  );
}